/**
 * Share to WhatsApp through the native OS share sheet (Windows "Paylaş" /
 * macOS / mobile) instead of opening the api.whatsapp.com / wa.me browser tab.
 *
 * Cascade:
 *   1. Platform can share files       -> share the PDF + text in the native sheet.
 *   2. Web Share exists (no file)     -> share text-only in the native sheet
 *                                        (the PDF, if any, is downloaded first so
 *                                        it isn't lost).
 *   3. No Web Share API at all        -> open wa.me as a last resort.
 *
 * IMPORTANT: Must be invoked from within a user gesture. When a PDF is involved,
 * either pre-fetch it before the click or accept a single quick await — the
 * transient activation that navigator.share requires lasts a few seconds.
 *
 * @returns {Promise<"file"|"text"|"cancelled"|"waurl">}
 */
export async function shareToWhatsApp({ text, title, file, waUrl, downloadName } = {}) {
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title });
        return "file";
      }
      // File can't be shared on this platform → still open the native sheet with
      // the text, but download the PDF first so the user can attach it manually.
      if (file && downloadName) downloadBlobFile(file, downloadName);
      await navigator.share({ text, title });
      return "text";
    } catch (e) {
      if (e && e.name === "AbortError") return "cancelled"; // user closed the sheet
      // Any other failure (e.g. lost activation) → fall through to wa.me.
    }
  }
  if (file && downloadName) downloadBlobFile(file, downloadName);
  if (waUrl) window.open(waUrl, "_blank");
  return "waurl";
}

function downloadBlobFile(file, name) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
